import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { downloadCsv } from '../lib/csv';
import { csvName, vatCsv } from '../lib/reportCsv';
import { formatCents, formatTimestamp } from '../lib/format';
import { periodRange, PERIOD_OPTIONS, type PeriodKey } from '../lib/period';
import { PageHelp } from '../components/PageHelp';
import type { Division, TaxAreaEntry } from '../lib/types';

interface Amounts {
  net: number;
  tax: number;
  gross: number;
}

interface VatResponse {
  vat: {
    divisions: { divisionId: string; name: string; income: Amounts; expense: Amounts }[];
    income: Amounts;
    expense: Amounts;
    finanzamt: { refunds: number; payments: number };
    saldo: Amounts;
  };
  /** D-24: Belege mit Umsatzsteuer auf einer Kostenstelle, auf der keine gebucht werden darf */
  violations: VatViolation[];
  divisions: Division[];
  taxAreas: TaxAreaEntry[];
  dataAsOf: string | null;
}

interface VatViolation {
  postingId: string;
  stichtag: string;
  voucherNumber?: string;
  text?: string;
  partner?: string;
  divisionId?: string;
  costCentreNumber?: string;
  costCentreName?: string;
  taxCents: number;
  /** vom Admin als korrekt markiert */
  accepted: boolean;
}

/** Belege mit Umsatzsteuer auf einer Kostenstelle ohne Freigabe (D-24). */
function ViolationTable({
  rows,
  divisions,
  action,
}: {
  rows: VatViolation[];
  divisions: Division[];
  action?: (row: VatViolation) => ReactNode;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Datum</TableCell>
          <TableCell>Beleg</TableCell>
          <TableCell>Partner / Text</TableCell>
          <TableCell>Abteilung</TableCell>
          <TableCell>Kostenstelle</TableCell>
          <TableCell {...num}>Umsatzsteuer</TableCell>
          {action && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((x) => (
          <TableRow key={x.postingId}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{germanDate(x.stichtag)}</TableCell>
            <TableCell>{x.voucherNumber}</TableCell>
            <TableCell>{x.partner ?? x.text}</TableCell>
            <TableCell>{divisions.find((d) => d.divisionId === x.divisionId)?.name}</TableCell>
            <TableCell>
              {x.costCentreNumber} {x.costCentreName}
            </TableCell>
            <TableCell {...num}>{euro(x.taxCents)}</TableCell>
            {action && <TableCell>{action(x)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const euro = (cents: number) => formatCents(cents);
const germanDate = (iso: string) => iso.split('-').reverse().join('.');
const num = { align: 'right' as const, sx: { whiteSpace: 'nowrap' as const } };

/**
 * D-21: Umsatzsteuer-Sicht für den Gesamtverein (Admin, Vorstand). Das Dashboard zeigt netto;
 * hier stehen Brutto, Umsatzsteuer und Netto nebeneinander (Brutto + USt = Netto), dazu
 * Erstattungen und Zahlungen des Finanzamts (Brutto = Netto) -- der Brutto-Saldo ist der
 * Geldfluss wie in der EÜR, der USt-Saldo die Zahllast.
 */
export default function UmsatzsteuerPage() {
  const today = new Date().toISOString().slice(0, 10);
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [period, setPeriod] = useState<PeriodKey>('jahr');
  const [taxArea, setTaxArea] = useState('');
  const [data, setData] = useState<VatResponse>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const { from, to } = periodRange(year, period, today);
  const query = useMemo(() => {
    const q: Record<string, string> = { from, to };
    if (taxArea) q.taxArea = taxArea;
    return q;
  }, [from, to, taxArea]);

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    api
      .get<VatResponse>(`/admin/report/vat?${new URLSearchParams(query)}`)
      .then(setData)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Auswertung konnte nicht geladen werden'),
      )
      .finally(() => setLoading(false));
  }, [query, reload]);

  /** D-24: Ausnahme setzen bzw. aufheben, danach neu laden. */
  async function accept(postingId: string, value: boolean) {
    try {
      await api.post('/admin/report/vat/exceptions', { postingId, accepted: value });
      setReload((r) => r + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    }
  }

  const v = data?.vat;
  const open = data?.violations.filter((x) => !x.accepted) ?? [];
  const accepted = data?.violations.filter((x) => x.accepted) ?? [];
  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Umsatzsteuer</Typography>
        <PageHelp text="Netto, Umsatzsteuer und Brutto für den Gesamtverein. Das Dashboard zeigt netto, weil die Umsatzsteuer nicht der Abteilung gehört: der Verein führt sie ans Finanzamt ab bzw. erhält Vorsteuer erstattet. Zahlungen und Erstattungen des Finanzamts stehen nur hier. Gerechnet wird von links nach rechts: Brutto + Umsatzsteuer = Netto. Eingenommene Umsatzsteuer (müssen wir abführen) ist deshalb negativ, gezahlte Vorsteuer (bekommen wir erstattet) positiv. Der Brutto-Saldo entspricht dem Geldfluss wie in der EÜR (dort zusätzlich Abschreibungen statt Anschaffungen), der Umsatzsteuer-Saldo der Zahllast nach Zahlungsdatum. Zahlungen an das und Erstattungen vom Finanzamt sind selbst nicht umsatzsteuerbar (Brutto = Netto)." />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          size="small"
          label="Jahr"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ minWidth: 110 }}
        >
          {Array.from({ length: thisYear - 2017 }, (_, i) => thisYear - i).map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Zeitraum"
          value={period}
          onChange={(e) => setPeriod(e.target.value as PeriodKey)}
          sx={{ minWidth: 160 }}
        >
          {PERIOD_OPTIONS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Steuerlicher Bereich"
          value={taxArea}
          onChange={(e) => setTaxArea(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Alle Bereiche</MenuItem>
          {(data?.taxAreas ?? []).map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {data && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Typography variant="body2" color="text.secondary">
            {germanDate(from)} bis {germanDate(to)} · Datenstand:{' '}
            {data.dataAsOf ? formatTimestamp(data.dataAsOf) : 'noch kein Import'}
          </Typography>
          <Box>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              disabled={!v || loading}
              onClick={() => v && downloadCsv(csvName('umsatzsteuer', query), vatCsv(v))}
            >
              Als CSV
            </Button>
          </Box>
        </Stack>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {open.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ mb: 1 }}>
            {open.length === 1 ? 'Ein Beleg trägt' : `${open.length} Belege tragen`} Umsatzsteuer
            auf einer Kostenstelle, auf der keine gebucht werden darf (erlaubt nur auf Kostenstellen
            mit dem Kennzeichen „USt erlaubt“, Seite „Zuordnungen“, Reiter Kostenstellen; im
            ideellen Bereich nie). Bitte in SevDesk Kostenstelle oder Steuersatz korrigieren – oder,
            wenn der Beleg so richtig ist, als korrekt markieren. Die Admins bekommen dazu eine
            Mail.
          </Typography>
          <ViolationTable
            rows={open}
            divisions={data!.divisions}
            action={
              isAdmin
                ? (x) => (
                    <Button size="small" onClick={() => void accept(x.postingId, true)}>
                      Als korrekt markieren
                    </Button>
                  )
                : undefined
            }
          />
        </Alert>
      )}
      {accepted.length > 0 && (
        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">
              Als korrekt markiert: {accepted.length} {accepted.length === 1 ? 'Beleg' : 'Belege'}{' '}
              mit Umsatzsteuer auf einer Kostenstelle ohne Freigabe
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <ViolationTable
              rows={accepted}
              divisions={data!.divisions}
              action={
                isAdmin
                  ? (x) => (
                      <Button size="small" onClick={() => void accept(x.postingId, false)}>
                        Markierung aufheben
                      </Button>
                    )
                  : undefined
              }
            />
          </AccordionDetails>
        </Accordion>
      )}
      {loading && !v && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {v && (
        <>
          <TableContainer component={Paper} sx={{ opacity: loading ? 0.5 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Verein gesamt</TableCell>
                  <TableCell {...num}>Brutto</TableCell>
                  <TableCell {...num}>Umsatzsteuer</TableCell>
                  <TableCell {...num}>Netto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AmountRow label="Einnahmen (Umsatzsteuer abzuführen)" a={v.income} />
                <AmountRow label="Ausgaben (Vorsteuer erstattungsfähig)" a={v.expense} />
                <AmountRow
                  label="Vom Finanzamt erstattet"
                  a={{ gross: v.finanzamt.refunds, tax: 0, net: v.finanzamt.refunds }}
                />
                <AmountRow
                  label="An das Finanzamt gezahlt"
                  a={{ gross: v.finanzamt.payments, tax: 0, net: v.finanzamt.payments }}
                />
                <AmountRow
                  label="Saldo (Umsatzsteuer = Zahllast: − abzuführen, + zu erstatten)"
                  a={v.saldo}
                  bold
                />
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} sx={{ opacity: loading ? 0.5 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={3} align="center">
                    Einnahmen
                  </TableCell>
                  <TableCell colSpan={3} align="center">
                    Ausgaben
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Abteilung</TableCell>
                  <TableCell {...num}>Brutto</TableCell>
                  <TableCell {...num}>USt</TableCell>
                  <TableCell {...num}>Netto</TableCell>
                  <TableCell {...num}>Brutto</TableCell>
                  <TableCell {...num}>Vorsteuer</TableCell>
                  <TableCell {...num}>Netto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {v.divisions.map((d) => (
                  <TableRow key={d.divisionId} hover>
                    <TableCell>{d.name}</TableCell>
                    <AmountCells a={d.income} />
                    <AmountCells a={d.expense} />
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <strong>Summe</strong>
                  </TableCell>
                  <AmountCells a={v.income} bold />
                  <AmountCells a={v.expense} bold />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Stack>
  );
}

/**
 * Brutto, Umsatzsteuer, Netto -- von links nach rechts gerechnet: Brutto + USt = Netto. Die
 * Umsatzsteuer steht deshalb aus Sicht des Finanzamts da: eingenommene USt (abzuführen) negativ,
 * gezahlte Vorsteuer (wird erstattet) positiv.
 */
function AmountCells({ a, bold }: { a: Amounts; bold?: boolean }) {
  return [a.gross, -a.tax, a.net].map((c, i) => (
    <TableCell key={i} {...num}>
      {bold ? <strong>{euro(c)}</strong> : euro(c)}
    </TableCell>
  ));
}

function AmountRow({ label, a, bold }: { label: string; a: Amounts; bold?: boolean }) {
  return (
    <TableRow>
      <TableCell>{bold ? <strong>{label}</strong> : label}</TableCell>
      <AmountCells a={a} bold={bold} />
    </TableRow>
  );
}
